import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable, startWith, Subject, Subscription, tap, withLatestFrom } from 'rxjs';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { DeviceService } from 'src/app/devices/services/device.service';
import { ConfirmationDialogService } from 'src/app/shared/services/confirmation-dialog.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import * as DeviceManufacturerActions from "../../stores/global/device-manufacturers/device-manufacturers-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { EditEvent, PageChangeEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import { ICreateDeviceManufacturerRequest, IUpdateDeviceManufacturerRequest, IDeviceManufacturer } from 'src/app/devices/models/device-manufacturer';
import { DeviceManufacturerValidator } from 'src/app/shared/validators/device-manufacturer-validator';
import { isLoadingSelector, deviceManufacturerSelector, deviceManufacturersTotalSelector } from 'src/app/stores/global/device-manufacturers/device-manufacturers-selectors';
import { SortDescriptor } from '@progress/kendo-data-query';
import { debug, RxJsLoggingLevel } from 'src/app/shared/rxjs-operators/debug-operator';


@Component({
    selector: 'app-manage-device-manufacturers',
    templateUrl: './manage-device-manufacturers.component.html',
    styleUrl: './manage-device-manufacturers.component.scss',
    standalone: false
})
export class ManageDeviceManufacturersComponent implements OnInit{


  constructor(    
    private deviceService: DeviceService,
    private store: Store<AppStateInterface>) {}

  // Static Variables
  private editedRowIndex: number;
  public formGroup: FormGroup;
  showDeleteModal : string;
  sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  //Getters
  get pageSizes(){
    return PageSize;
  }


  // (Behaviour)Subjects
  pageChangeSub$ = new Subject<PageChangeEvent>();
  createItemSub$ = new Subject<ICreateDeviceManufacturerRequest>();
  updateItemSub$ = new Subject<IUpdateDeviceManufacturerRequest>();
  deleteItemSub$ = new Subject<string>();
  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: this.pageSizes.Small,
      skip: 0,
    }
  );

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    orderBy: ['name asc'] // only needed for gizmo projects
  });



  // View Model
  vm$ = combineLatest([
    this.store.pipe(select(deviceManufacturerSelector)),
    this.store.pipe(select(deviceManufacturersTotalSelector)),
    this.createItemSub$.pipe(
      tap(request => this.store.dispatch(DeviceManufacturerActions.createDeviceManufacturer({deviceManufacturer: request}))),
      startWith(null),
    ),
    this.updateItemSub$.pipe(
      tap(request => this.store.dispatch(DeviceManufacturerActions.updateDeviceManufacturer({deviceManufacturer: request}))),
      startWith(null),
    ),
    this.deleteItemSub$.pipe(
      tap(id => this.store.dispatch(DeviceManufacturerActions.deleteDeviceManufacturer({id}))),
      startWith(null),
    ),
    this.gridState$,
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
      })),
      tap(data => {
        const pageNumber = Math.round(
          (data.pageChangeEvent.skip + data.pageChangeEvent.take) /
          data.pageChangeEvent.take
        );
        this.gridState$.next({ skip: data.pageChangeEvent.skip, pageSize: data.pageChangeEvent.take});
        this.searchRequest$.next({
          ...data.searchRequest,
          pageNumber: pageNumber,
          pageSize: data.pageChangeEvent.take,
        });
      }),
      startWith({skip: 0, take: 10})
    ),
    this.searchRequest$.pipe(
      tap(request => this.store.dispatch(DeviceManufacturerActions.searchDeviceManufacturers({request})))
    )
  ]).pipe(
    map(([
      deviceManufacturers,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    ]) => ({
      deviceManufacturers,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    }))
  );

  ngOnInit(): void {}

  createFormGroup(deviceManufacturer : IDeviceManufacturer = null){
    return new FormGroup({
      id: new UntypedFormControl(deviceManufacturer?.id ?? null),
      name: new UntypedFormControl(deviceManufacturer?.name ?? null, {
        validators: Validators.required,
        asyncValidators: DeviceManufacturerValidator(this.deviceService),
        updateOn: 'change'}),
    });
  }

  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
    this.formGroup = null;
  }

  addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup();

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  saveHandler(event){
    if(this.formGroup.value.id == null){
      this.createItemSub$.next({...this.formGroup.value})
    } else {
      this.updateItemSub$.next({...this.formGroup.value})
    }

    this.closeEditor(event.sender);
  }

  deleteHandler(event){
    this.deleteItemSub$.next(event.dataItem.id)
    this.closeEditor(event.sender);
  }

  sortChange(sort: SortDescriptor[], searchRequest: IBaseSearchRequest): void {
    // this is needed for gizmo projects
    this.sort = sort;
    this.searchRequest$.next({...searchRequest, orderBy: sort.map(x => `${x.field} ${x.dir}`)})

    // this is needed for garax projects
    // if (sort[0].field !== "fileId") {
    //   this.sort = sort;
    // } else {
    //   this.sort = [];
    // }
    //this.searchRequest$.next({...searchRequest, sort: this.sort})
  }

  onDelete(id: string) {
    this.deleteItemSub$.next(id);
    this.showDeleteModal = null;
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
